import { ParallelismMode } from '../shared/models/execution-details.models';
import type { ParallelConfiguration } from '../shared/services/parser.service';

export class MissingSequentialBaselineError extends Error {
  constructor(
    public regionId: string,
    public parallelismMode: ParallelismMode,
  ) {
    super(buildMissingBaselineMessage(parallelismMode, regionId));
    this.name = 'MissingSequentialBaselineError';
  }
}

export function isSequentialConfig(p: ParallelConfiguration): boolean {
  return p.totalCores === 1 && p.axisValue === 1;
}

export function hasSequentialBaseline(
  measurements: Array<{ parallelism: ParallelConfiguration }>
): boolean {
  return measurements.some(m => isSequentialConfig(m.parallelism));
}

export function buildGlobalMissingBaselineMessage(
  mode: ParallelismMode,
  command?: string | null
): string {
  const why = 'Speedup (S = T_seq / T_par) and efficiency (E = T_seq / (p * T_par)) need T_seq (parallelism == 1).';
  const fixes: Record<ParallelismMode, string> = {
    omp: 'Re-run including 1 core, e.g.: pascalanalyzer ./main -c 1,2,4 -i <inputs> -o out.json',
    mpi: 'Re-run including 1 rank, e.g.: pascalanalyzer ./run --ranks 1,2,4 -i <inputs> -o out.json',
    hybrid: 'Re-run including the 1x1 topology, e.g.: pascalanalyzer ./run --ranks 1,2 -c 1,4 -o out.json',
    unknown: 'Re-run including parallelism == 1 (add 1 to cores/ranks list).',
  };
  const cmdLine = command ? `\nDetected command: ${command}` : '';
  return `Missing sequential baseline (no 1-core execution found). Charts are blocked.\n${why}\n${fixes[mode]}${cmdLine}`;
}

export function buildMissingBaselineMessage(mode: ParallelismMode, regionId: string): string {
  const why = 'Speedup (S = T_seq / T_par) and efficiency (E = T_seq / (p * T_par)) need T_seq (parallelism == 1).';
  const fixes = {
    omp: 'Re-run including 1 core, e.g.: pascalanalyzer ./main -c 1,2,4 -i <inputs> -o out.json',
    mpi: 'Re-run including 1 rank, e.g.: pascalanalyzer ./run --ranks 1,2,4 -i <inputs> -o out.json',
    hybrid: 'Re-run including the 1x1 topology, e.g.: pascalanalyzer ./run --ranks 1,2 -c 1,4 -o out.json',
    unknown: 'Re-run including parallelism == 1 (add 1 to cores/ranks list).',
  } as const;
  return `Missing sequential baseline in region ${regionId} (no 1-core execution). Charts blocked.\n${why}\n${fixes[mode]}`;
}